/**
 * Menu card templates — SVG, pure vector so they print crisply at any DPI.
 *
 * Five decorative art-deco styles + one "plain (legacy)" repro of the old
 * SSRS card. Each renders at one of two sizes:
 *   - A6 portrait  (105×148mm) — the original single-column card.
 *   - A5 landscape (210×148mm) — a two-panel card (guest/service left, menu
 *     right), via the shared `<A5CardContent>`.
 * Size is a prop (`size`), orthogonal to the decorative style.
 *
 * Each template is a React component that returns an <svg> element. The
 * decorative styles share the same inner content layout per size (the A6
 * `<CardContent>` / the A5 `<A5CardContent>`) — only the borders/motifs
 * differ — so the kitchen team always knows where to look on the card.
 *
 * Used at three scales:
 *   1. As a dropdown thumbnail in the modal (~200×280px, screen render).
 *   2. As the on-screen preview at print time (~80% of viewport).
 *   3. As an A6 PDF page via svg2pdf — see ./pdfBuilder.ts.
 *
 * ──────────────────────────────────────────────────────────────────────
 *  Coordinate system (mm, A6 portrait, viewBox="0 0 105 148"):
 *
 *     0 ┌─────────────── 105
 *       │  decorative   │     ← 0..18  : header zone (template-specific)
 *    18 ├───────────────┤
 *       │  service info │     ← 18..38 : service date/time/description
 *    38 ├───────────────┤
 *       │  guest info   │     ← 38..62 : name, coach, table, seats
 *    62 ├───────────────┤
 *       │  menu list    │     ← 62..125 : the pre-ordered courses
 *   125 ├───────────────┤
 *       │  diet banner? │     ← 125..140 : optional dietary flag
 *   140 ├───────────────┤
 *       │  decorative   │     ← 140..148 : footer zone (template-specific)
 *   148 └───────────────┘
 *
 *  All templates leave the inner content zone (10..95 horizontally,
 *  18..140 vertically) free for `<CardContent>` to paint into.
 *
 *  PDF font note: svg2pdf maps SVG `font-family` to jsPDF fonts by name.
 *  jsPDF ships with helvetica / times / courier built-in. We use Times
 *  (a serif) throughout — clean, classic, prints well in monochrome.
 * ──────────────────────────────────────────────────────────────────────
 */

import type { CSSProperties, ReactElement, ReactNode } from "react";
import type {
  CardSize,
  MenuCardBooking,
  PrintMode,
  TemplateStyle,
} from "../types";

// ===========================================================================
// Palettes
// ===========================================================================

interface Palette {
  paper: string;
  ink: string;
  inkSoft: string;
  rule: string;
  accent: string;       // primary decorative colour (gold in colour mode)
  accentDeep: string;   // contrasting accent (Pullman green)
  diet: string;         // dietary banner background
  dietInk: string;
  isBw: boolean;        // pure black-&-white mode → solid black, no partial opacity
}

const PALETTE_COLOUR: Palette = {
  paper: "#fbf3df",
  ink: "#2a2014",
  inkSoft: "#5e4a2b",
  rule: "#7e6a3a",
  accent: "#c89b3c",      // Pullman gold
  accentDeep: "#0b3d2e",  // Pullman green
  diet: "#7a2828",
  dietInk: "#fbf3df",
  isBw: false,
};

const PALETTE_BW: Palette = {
  // Pure black & white — no greys, no partial opacity. Prints crisp on cream card
  // stock (a near-black grey looked washed-out / "grey on cream").
  paper: "#ffffff",
  ink: "#000000",
  inkSoft: "#000000",
  rule: "#000000",
  accent: "#000000",
  accentDeep: "#000000",
  diet: "#000000",
  dietInk: "#ffffff",
  isBw: true,
};

function paletteFor(mode: PrintMode): Palette {
  return mode === "bw" ? PALETTE_BW : PALETTE_COLOUR;
}

// ===========================================================================
// Helpers — text wrapping, menu parsing, dietary detection.
//
// SVG <text> doesn't auto-wrap, so we pre-compute lines with a forgiving
// character-count heuristic (no font metrics — Times average char width
// at our sizes is consistent enough that this works in practice).
// ===========================================================================

interface ParsedMenuLine {
  qty: string;     // "2 x"
  course: string;  // "Starter" or ""
  item: string;    // rest
  raw: string;
}

function parseMenuLines(menus: string): ParsedMenuLine[] {
  return menus
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((raw) => {
      const m = raw.match(/^(\d+\s*x)\s+(.*)$/i);
      if (!m) return { qty: "", course: "", item: raw, raw };
      const qty = m[1].toLowerCase();
      const rest = m[2];
      const dashIx = rest.indexOf(" - ");
      if (dashIx > 0) {
        return {
          qty,
          course: rest.slice(0, dashIx).trim(),
          item: rest.slice(dashIx + 3).trim(),
          raw,
        };
      }
      return { qty, course: "", item: rest.trim(), raw };
    });
}

function shortCarriage(name: string): string {
  return name.replace(/^Pullman\s*-\s*/i, "").trim();
}

function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if (!cur) {
      cur = w;
    } else if ((cur + " " + w).length <= maxChars) {
      cur = cur + " " + w;
    } else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

export function cleanBookingNotes(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .replace(/\b(pi|pm|ch|seti|cs)_[A-Za-z0-9]+\b/g, "")
    .replace(/\bSTRIPE[-_][A-Z]+\b/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-–—,]+|[\s\-–—,]+$/g, "")
    .trim();
}

/** An edited dietary comment (cache.MenuCardDietaryOverrides) counts as a dietary flag. */
export function hasDietaryOverride(booking: MenuCardBooking): boolean {
  return Boolean((booking.dietary_override ?? "").trim());
}

/** The card's dietary flag: MERAC's, or the catering team's edited comment. */
function flagsDietary(booking: MenuCardBooking): boolean {
  return booking.has_dietary_notes || hasDietaryOverride(booking);
}

function dietarySummary(booking: MenuCardBooking): string {
  // The edited comment wins outright — it is what the team decided the card should say.
  const override = (booking.dietary_override ?? "").trim();
  if (override) return override.length > 220 ? override.slice(0, 217) + "…" : override;
  const notes = cleanBookingNotes(booking.booking_notes);
  if (notes) return notes.length > 220 ? notes.slice(0, 217) + "…" : notes;
  const lines = booking.menus.split(/\r?\n/);
  const flagged = lines.filter((l) =>
    /(\bGF\b|\bVG\b|vegan|gluten|dairy|nut|allerg)/i.test(l)
  );
  if (flagged.length) return flagged.map((l) => l.trim()).join(" · ");
  return "Dietary requirement on this booking — please confirm with the steward.";
}

// ===========================================================================
// Shared inner content — same on all five templates.
// ===========================================================================

// ---------------------------------------------------------------------------
// Batch-level "scale food lines to fit" flag. `renderTemplate` sets this from
// the print options immediately before each card renders (synchronous — no
// await in between), so the shared CardContent / A5CardContent can scale the
// menu lines without threading a prop through all seven templates. The option
// is uniform for a whole print run, so a module-level flag is safe here.
// ---------------------------------------------------------------------------
let scaleFoodToFitActive = false;

interface CardContentProps {
  booking: MenuCardBooking;
  palette: Palette;
  highlightDietary: boolean;
}

const CONTENT_LEFT = 12;
const CONTENT_RIGHT = 93;
const CONTENT_W = CONTENT_RIGHT - CONTENT_LEFT;

function CardContent({ booking, palette: p, highlightDietary }: CardContentProps) {
  const lines = parseMenuLines(booking.menus);
  const carriage = shortCarriage(booking.carriage);
  const descLines = wrapText(booking.journey_description, 50);

  // Menu sizing. Classic (fixed): 11 lines fit comfortably at 3.4mm; squeeze
  // for more. "Scale food lines to fit" (opt-in): distribute the lines across
  // the whole menu area (y 76 → the dietary banner / footer) so a short menu
  // prints larger and spread out instead of bunched at the top.
  const showDietary = highlightDietary && flagsDietary(booking);
  let menuMaxLines: number;
  let menuLineH: number;
  let menuFontSize: number;
  if (scaleFoodToFitActive) {
    const yTop = 76;
    const yBottom = showDietary ? 124 : 140; // clear the dietary banner / footer
    const avail = yBottom - yTop;
    const MIN_LH = 3.0, MAX_LH = 8.5, MIN_FS = 2.4, MAX_FS = 3.4;
    menuMaxLines = Math.max(1, Math.floor(avail / MIN_LH));
    const n = Math.min(lines.length, menuMaxLines) || 1;
    menuLineH = Math.min(MAX_LH, Math.max(MIN_LH, avail / n));
    menuFontSize = Math.min(MAX_FS, Math.max(MIN_FS, menuLineH * 0.8));
  } else {
    menuMaxLines = 14;
    menuLineH = lines.length > 11 ? 3.0 : 3.4;
    menuFontSize = lines.length > 11 ? 2.4 : 2.7;
  }
  const visibleMenuLines = lines.slice(0, menuMaxLines);
  const menuTruncated = lines.length > menuMaxLines;

  return (
    <g>
      {/* ── Service header (date + description) ─────────────────────── */}
      <text
        x={52.5}
        y={22}
        textAnchor="middle"
        fontFamily="Times"
        fontSize="3.6"
        fontWeight="bold"
        fill={p.ink}
      >
        {booking.service_day_label}
      </text>
      <text
        x={52.5}
        y={27}
        textAnchor="middle"
        fontFamily="Times"
        fontSize="2.6"
        fontStyle="italic"
        fill={p.inkSoft}
      >
        {descLines[0] ?? booking.journey_description}
      </text>
      {descLines[1] && (
        <text
          x={52.5}
          y={31}
          textAnchor="middle"
          fontFamily="Times"
          fontSize="2.6"
          fontStyle="italic"
          fill={p.inkSoft}
        >
          {descLines[1]}
        </text>
      )}

      {/* ── Hairline rule ─────────────────────────────────────────── */}
      <line
        x1={CONTENT_LEFT + 8}
        y1={35}
        x2={CONTENT_RIGHT - 8}
        y2={35}
        stroke={p.rule}
        strokeWidth="0.25"
      />

      {/* ── Guest details (name + coach/table/seats) ────────────────── */}
      <g fontFamily="Times" fill={p.ink}>
        <text
          x={CONTENT_LEFT}
          y={43}
          fontSize="2.4"
          fontStyle="italic"
          letterSpacing="0.4"
          fill={p.inkSoft}
        >
          GUEST
        </text>
        <text
          x={CONTENT_LEFT}
          y={49}
          fontSize="3.6"
          fontWeight="bold"
        >
          {booking.account_name.length > 30
            ? booking.account_name.slice(0, 28) + "…"
            : booking.account_name}
        </text>

        {/* Coach + Table + Seats row */}
        <g fontSize="2.4" fontStyle="italic" letterSpacing="0.4" fill={p.inkSoft}>
          <text x={CONTENT_LEFT} y={56}>COACH</text>
          <text x={CONTENT_LEFT + 30} y={56}>TABLE</text>
          <text x={CONTENT_LEFT + 50} y={56}>SEATS</text>
        </g>
        <g fontSize="2.9">
          <text x={CONTENT_LEFT} y={61} fontWeight="bold">
            {carriage}
          </text>
          <text x={CONTENT_LEFT + 30} y={61} fontWeight="bold">
            {booking.table_name}
          </text>
          <text x={CONTENT_LEFT + 50} y={61}>
            {booking.seats}
          </text>
        </g>

        {booking.footfall != null && booking.footfall > 0 && (
          <text
            x={CONTENT_RIGHT}
            y={61}
            textAnchor="end"
            fontSize="2.4"
            fontStyle="italic"
            fill={p.inkSoft}
          >
            {booking.footfall} {booking.footfall === 1 ? "diner" : "diners"}
          </text>
        )}
      </g>

      {/* ── "MENU" pill ──────────────────────────────────────────── */}
      <rect
        x={47}
        y={66}
        width={11}
        height={4.5}
        rx={0.5}
        fill={p.accent}
      />
      <text
        x={52.5}
        y={69.4}
        textAnchor="middle"
        fontFamily="Times"
        fontSize="2.6"
        fontWeight="bold"
        letterSpacing="0.6"
        fill={p.paper}
      >
        MENU
      </text>

      {/* ── Menu list ────────────────────────────────────────────── */}
      <g fontFamily="Times" fontSize={menuFontSize}>
        {visibleMenuLines.map((ln, i) => {
          const y = 76 + i * menuLineH;
          const item = ln.item.length > 42 ? ln.item.slice(0, 40) + "…" : ln.item;
          return (
            <g key={i}>
              <text
                x={CONTENT_LEFT + 2}
                y={y}
                fontWeight="bold"
                fill={p.accentDeep}
              >
                {ln.qty || "•"}
              </text>
              {ln.course && (
                <text
                  x={CONTENT_LEFT + 11}
                  y={y}
                  fontStyle="italic"
                  fill={p.inkSoft}
                >
                  {ln.course}
                </text>
              )}
              <text
                x={CONTENT_LEFT + (ln.course ? 26 : 11)}
                y={y}
                fill={p.ink}
              >
                {item}
              </text>
            </g>
          );
        })}
        {menuTruncated && (
          <text
            x={CONTENT_LEFT + 2}
            y={76 + visibleMenuLines.length * menuLineH}
            fontSize="2.0"
            fontStyle="italic"
            fill={p.inkSoft}
          >
            (… {lines.length - menuMaxLines} more — see full booking)
          </text>
        )}
      </g>

      {/* ── Dietary banner (only when flagged AND option enabled) ──── */}
      {highlightDietary && flagsDietary(booking) && (
        <DietaryBanner booking={booking} palette={p} />
      )}

      {/* ── Footer micro-row: booking ref left, departure time right ─ */}
      <text
        x={CONTENT_LEFT}
        y={144.5}
        fontFamily="Times"
        fontSize="1.9"
        fill={p.inkSoft}
      >
        Booking {booking.booking_reference}
      </text>
      <text
        x={CONTENT_RIGHT}
        y={144.5}
        textAnchor="end"
        fontFamily="Times"
        fontSize="1.9"
        fill={p.inkSoft}
      >
        Departs {booking.service_time_label.slice(-5)}
      </text>
    </g>
  );
}

function DietaryBanner({
  booking,
  palette: p,
}: {
  booking: MenuCardBooking;
  palette: Palette;
}) {
  const summary = dietarySummary(booking);
  const lines = wrapText(summary, 56);
  const top = 127;
  return (
    <g>
      <rect
        x={CONTENT_LEFT - 1}
        y={top}
        width={CONTENT_W + 2}
        height={11}
        rx={0.5}
        fill={p.diet}
      />
      <text
        x={CONTENT_LEFT + 1}
        y={top + 3.4}
        fontFamily="Times"
        fontSize="2.4"
        fontWeight="bold"
        letterSpacing="0.6"
        fill={p.dietInk}
      >
        DIETARY NOTES
      </text>
      {lines.slice(0, 2).map((ln, i) => (
        <text
          key={i}
          x={CONTENT_LEFT + 1}
          y={top + 6.6 + i * 2.4}
          fontFamily="Times"
          fontSize="2.0"
          fill={p.dietInk}
        >
          {ln.length > 60 ? ln.slice(0, 58) + "…" : ln}
        </text>
      ))}
    </g>
  );
}

// ===========================================================================
// Shared inner content — A5 LANDSCAPE two-panel variant.
//
// Same fields as the A6 CardContent, but laid out across a 210×148 sheet:
// guest + service detail fill the left panel, and the pre-ordered menu gets
// its own full-height column on the right (room for ~24 lines). Mirrors the
// layout intent of the legacy "Menu Cards - A5" RDL, in NYMR house style.
//
//   0                    104                       210
//   ┌────────────────────┊────────────────────────┐
//   │  service header     ┊   ▓MENU▓                │  ← 20
//   │  ─────────          ┊   2 x Starter — Soup    │
//   │  GUEST              ┊   2 x Main — Beef …      │
//   │  Name               ┊   …                      │
//   │  COACH TABLE SEATS  ┊                          │
//   │  [dietary banner]   ┊                          │
//   │  Booking 1234       ┊            Departs 12:35 │  ← 148
//   └────────────────────┊────────────────────────┘
// ===========================================================================

// Left panel content column.
const A5_L = 16;
const A5_L_R = 98;
// Right (menu) panel content column.
const A5_R = 112;
const A5_R_R = 196;
// Panel divider.
const A5_DIVIDER_X = 104;

function A5CardContent({ booking, palette: p, highlightDietary }: CardContentProps) {
  const lines = parseMenuLines(booking.menus);
  const carriage = shortCarriage(booking.carriage);
  const descLines = wrapText(booking.journey_description, 46);

  // A5 gives the menu its own full-height column — room for ~24 lines. Classic
  // fixed sizing, or "scale food lines to fit" across the right panel
  // (y 33 → ~134mm) when the option is on. The dietary banner sits in the LEFT
  // panel, so it never squeezes this column.
  let menuMaxLines: number;
  let menuLineH: number;
  let menuFontSize: number;
  if (scaleFoodToFitActive) {
    const yTop = 33;
    const yBottom = 134;
    const avail = yBottom - yTop;
    const MIN_LH = 3.6, MAX_LH = 9.0, MIN_FS = 3.0, MAX_FS = 4.6;
    menuMaxLines = Math.max(1, Math.floor(avail / MIN_LH));
    const n = Math.min(lines.length, menuMaxLines) || 1;
    menuLineH = Math.min(MAX_LH, Math.max(MIN_LH, avail / n));
    menuFontSize = Math.min(MAX_FS, Math.max(MIN_FS, menuLineH * 0.75));
  } else {
    menuMaxLines = 24;
    menuLineH = lines.length > 18 ? 4.0 : 4.6;
    menuFontSize = lines.length > 18 ? 3.0 : 3.4;
  }
  const visibleMenuLines = lines.slice(0, menuMaxLines);
  const menuTruncated = lines.length > menuMaxLines;

  const showDietary = highlightDietary && flagsDietary(booking);

  return (
    <g>
      {/* ── Panel divider ───────────────────────────────────────────── */}
      <line
        x1={A5_DIVIDER_X}
        y1={20}
        x2={A5_DIVIDER_X}
        y2={138}
        stroke={p.rule}
        strokeWidth="0.3"
      />

      {/* ── LEFT PANEL: service header ──────────────────────────────── */}
      <text x={A5_L} y={27} fontFamily="Times" fontSize="4.4" fontWeight="bold" fill={p.ink}>
        {booking.service_day_label}
      </text>
      <text x={A5_L} y={33} fontFamily="Times" fontSize="3.0" fontStyle="italic" fill={p.inkSoft}>
        {descLines[0] ?? booking.journey_description}
      </text>
      {descLines[1] && (
        <text x={A5_L} y={37.5} fontFamily="Times" fontSize="3.0" fontStyle="italic" fill={p.inkSoft}>
          {descLines[1]}
        </text>
      )}

      {/* Hairline rule under the header */}
      <line x1={A5_L} y1={43} x2={A5_L_R} y2={43} stroke={p.rule} strokeWidth="0.25" />

      {/* ── LEFT PANEL: guest details ───────────────────────────────── */}
      <g fontFamily="Times" fill={p.ink}>
        <text x={A5_L} y={53} fontSize="3.0" fontStyle="italic" letterSpacing="0.5" fill={p.inkSoft}>
          GUEST
        </text>
        <text x={A5_L} y={61} fontSize="5.0" fontWeight="bold">
          {booking.account_name.length > 32
            ? booking.account_name.slice(0, 30) + "…"
            : booking.account_name}
        </text>

        <g fontSize="3.0" fontStyle="italic" letterSpacing="0.5" fill={p.inkSoft}>
          <text x={A5_L} y={73}>COACH</text>
          <text x={A5_L + 34} y={73}>TABLE</text>
          <text x={A5_L + 56} y={73}>SEATS</text>
        </g>
        <g fontSize="3.6">
          <text x={A5_L} y={79} fontWeight="bold">{carriage}</text>
          <text x={A5_L + 34} y={79} fontWeight="bold">{booking.table_name}</text>
          <text x={A5_L + 56} y={79}>{booking.seats}</text>
        </g>

        {booking.footfall != null && booking.footfall > 0 && (
          <text x={A5_L} y={90} fontSize="3.2" fontStyle="italic" fill={p.inkSoft}>
            {booking.footfall} {booking.footfall === 1 ? "diner" : "diners"} at this table
          </text>
        )}
      </g>

      {/* Dietary banner — sits in the left panel's lower area when flagged */}
      {showDietary && (
        <A5DietaryBanner booking={booking} palette={p} left={A5_L} right={A5_L_R} top={99} />
      )}

      {/* ── RIGHT PANEL: "MENU" heading ─────────────────────────────── */}
      <rect
        x={(A5_R + A5_R_R) / 2 - 8}
        y={20}
        width={16}
        height={5.6}
        rx={0.6}
        fill={p.accent}
      />
      <text
        x={(A5_R + A5_R_R) / 2}
        y={24}
        textAnchor="middle"
        fontFamily="Times"
        fontSize="3.2"
        fontWeight="bold"
        letterSpacing="0.8"
        fill={p.paper}
      >
        MENU
      </text>

      {/* ── RIGHT PANEL: menu list ──────────────────────────────────── */}
      <g fontFamily="Times" fontSize={menuFontSize}>
        {visibleMenuLines.map((ln, i) => {
          const y = 33 + i * menuLineH;
          const item = ln.item.length > 46 ? ln.item.slice(0, 44) + "…" : ln.item;
          return (
            <g key={i}>
              <text x={A5_R} y={y} fontWeight="bold" fill={p.accentDeep}>
                {ln.qty || "•"}
              </text>
              {ln.course && (
                <text x={A5_R + 12} y={y} fontStyle="italic" fill={p.inkSoft}>
                  {ln.course}
                </text>
              )}
              <text x={A5_R + (ln.course ? 30 : 12)} y={y} fill={p.ink}>
                {item}
              </text>
            </g>
          );
        })}
        {menuTruncated && (
          <text
            x={A5_R}
            y={33 + visibleMenuLines.length * menuLineH}
            fontSize="2.6"
            fontStyle="italic"
            fill={p.inkSoft}
          >
            (… {lines.length - menuMaxLines} more — see full booking)
          </text>
        )}
      </g>

      {/* ── Footer micro-row: booking ref left, departure right ─────── */}
      <text x={A5_L} y={144.5} fontFamily="Times" fontSize="2.4" fill={p.inkSoft}>
        Booking {booking.booking_reference}
      </text>
      <text x={A5_R_R} y={144.5} textAnchor="end" fontFamily="Times" fontSize="2.4" fill={p.inkSoft}>
        Departs {booking.service_time_label.slice(-5)}
      </text>
    </g>
  );
}

function A5DietaryBanner({
  booking,
  palette: p,
  left,
  right,
  top,
}: {
  booking: MenuCardBooking;
  palette: Palette;
  left: number;
  right: number;
  top: number;
}) {
  const summary = dietarySummary(booking);
  const lines = wrapText(summary, 44);
  const w = right - left + 2;
  return (
    <g>
      <rect x={left - 1} y={top} width={w} height={30} rx={0.6} fill={p.diet} />
      <text
        x={left + 1.5}
        y={top + 5}
        fontFamily="Times"
        fontSize="3.0"
        fontWeight="bold"
        letterSpacing="0.8"
        fill={p.dietInk}
      >
        DIETARY NOTES
      </text>
      {lines.slice(0, 6).map((ln, i) => (
        <text
          key={i}
          x={left + 1.5}
          y={top + 9.8 + i * 3.4}
          fontFamily="Times"
          fontSize="2.6"
          fill={p.dietInk}
        >
          {ln.length > 48 ? ln.slice(0, 46) + "…" : ln}
        </text>
      ))}
    </g>
  );
}

// ===========================================================================
// Common SVG wrapper — every template uses this.
// ===========================================================================

interface CardSvgProps {
  children: ReactNode;
  palette: Palette;
  /** Card size. A6 = 105×148 portrait; A5 = 210×148 landscape. Defaults A6. */
  size?: CardSize;
  /**
   * Forwarded to the <svg> element. Used so the modal preview can size the
   * card to a panel; PDF generation queries the underlying DOM directly so
   * this is irrelevant in that path.
   */
  style?: CSSProperties;
}

function CardSvg({ children, palette: p, size = "a6", style }: CardSvgProps) {
  // A5 landscape is exactly double the A6 width at the same height, so the
  // svg2pdf 1:1 mm mapping still holds — we just widen the viewBox and page.
  const w = size === "a5" ? 210 : 105;
  const h = 148;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      // Width/height in mm so svg2pdf maps coordinates 1:1 onto the page.
      width={`${w}mm`}
      height={`${h}mm`}
      style={style}
    >
      <rect x={0} y={0} width={w} height={h} fill={p.paper} />
      {children}
    </svg>
  );
}

// ===========================================================================
// Common props for every template renderer.
// ===========================================================================

export interface TemplateProps {
  booking: MenuCardBooking;
  printMode: PrintMode;
  highlightDietary: boolean;
  /** Card size — A6 portrait (default) or A5 landscape. */
  size?: CardSize;
  /** Optional inline style for the wrapper <svg>. */
  style?: CSSProperties;
}

// ===========================================================================
// 1. Pullman Classic — gold chevrons + arched header.
// ===========================================================================

export function TemplatePullmanClassic({
  booking,
  printMode,
  highlightDietary,
  size = "a6",
  style,
}: TemplateProps) {
  const p = paletteFor(printMode);

  if (size === "a5") {
    return (
      <CardSvg palette={p} size="a5" style={style}>
        {/* Top + bottom double rules */}
        <line x1={8} y1={7} x2={202} y2={7} stroke={p.rule} strokeWidth="0.4" />
        <line x1={8} y1={7.8} x2={202} y2={7.8} stroke={p.rule} strokeWidth="0.15" />
        <line x1={8} y1={141} x2={202} y2={141} stroke={p.rule} strokeWidth="0.4" />
        <line x1={8} y1={140.2} x2={202} y2={140.2} stroke={p.rule} strokeWidth="0.15" />

        {/* PULLMAN wordmark + railway line, top centre */}
        <text x={105} y={12} textAnchor="middle" fontFamily="Times" fontSize="4.6" fontWeight="bold" letterSpacing="3" fill={p.accent}>
          PULLMAN
        </text>
        <text x={105} y={16.5} textAnchor="middle" fontFamily="Times" fontSize="2.6" fontWeight="bold" letterSpacing="0.6" fill={p.accentDeep}>
          NORTH YORKSHIRE MOORS RAILWAY
        </text>

        {/* Corner chevrons */}
        {[
          { x: 8, y: 11, sx: 1, sy: 1 },
          { x: 202, y: 11, sx: -1, sy: 1 },
          { x: 8, y: 137, sx: 1, sy: -1 },
          { x: 202, y: 137, sx: -1, sy: -1 },
        ].map((c, i) => (
          <g key={i} transform={`translate(${c.x} ${c.y}) scale(${c.sx} ${c.sy})`}>
            <path d="M 0 0 L 6 0 L 0 6 Z" fill={p.accent} />
            <path d="M 1 1.5 L 4.5 1.5 L 1 5 Z" fill={p.paper} />
            <path d="M 1.6 2.6 L 3.6 2.6 L 1.6 4.6 Z" fill={p.accent} />
          </g>
        ))}

        {/* Vertical accent rules on the far edges */}
        <line x1={8} y1={20} x2={8} y2={132} stroke={p.accent} strokeWidth="0.6" />
        <line x1={202} y1={20} x2={202} y2={132} stroke={p.accent} strokeWidth="0.6" />
        <line x1={6.4} y1={20} x2={6.4} y2={132} stroke={p.accent} strokeWidth="0.15" />
        <line x1={203.6} y1={20} x2={203.6} y2={132} stroke={p.accent} strokeWidth="0.15" />

        {/* Bottom centre rosette */}
        <circle cx={105} cy={144.5} r={1.4} fill={p.accent} />
        <circle cx={105} cy={144.5} r={0.55} fill={p.paper} />

        <A5CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
      </CardSvg>
    );
  }

  return (
    <CardSvg palette={p} style={style}>
      {/* Arched header panel */}
      <path
        d="M 6 4 L 99 4 Q 99 13 95 13 L 10 13 Q 6 13 6 4 Z"
        fill={p.accent}
      />
      <text
        x={52.5}
        y={9.5}
        textAnchor="middle"
        fontFamily="Times"
        fontSize="4.2"
        fontWeight="bold"
        letterSpacing="2.2"
        fill={p.paper}
      >
        PULLMAN
      </text>

      {/* Sub-header — railway company line */}
      <text
        x={52.5}
        y={17}
        textAnchor="middle"
        fontFamily="Times"
        fontSize="2.8"
        fontWeight="bold"
        letterSpacing="0.6"
        fill={p.accentDeep}
      >
        NORTH YORKSHIRE MOORS RAILWAY
      </text>

      {/* Double rule under header */}
      <line x1={6} y1={14.6} x2={99} y2={14.6} stroke={p.rule} strokeWidth="0.4" />
      <line x1={6} y1={15.4} x2={99} y2={15.4} stroke={p.rule} strokeWidth="0.15" />

      {/* Corner chevrons — four nested triangles */}
      {[
        { x: 6, y: 19, sx: 1, sy: 1 },
        { x: 99, y: 19, sx: -1, sy: 1 },
        { x: 6, y: 138, sx: 1, sy: -1 },
        { x: 99, y: 138, sx: -1, sy: -1 },
      ].map((c, i) => (
        <g key={i} transform={`translate(${c.x} ${c.y}) scale(${c.sx} ${c.sy})`}>
          <path d="M 0 0 L 6 0 L 0 6 Z" fill={p.accent} />
          <path d="M 1 1.5 L 4.5 1.5 L 1 5 Z" fill={p.paper} />
          <path d="M 1.6 2.6 L 3.6 2.6 L 1.6 4.6 Z" fill={p.accent} />
        </g>
      ))}

      {/* Vertical decorative rules linking the corners */}
      <line x1={6} y1={28} x2={6} y2={130} stroke={p.accent} strokeWidth="0.6" />
      <line x1={99} y1={28} x2={99} y2={130} stroke={p.accent} strokeWidth="0.6" />
      <line x1={4.4} y1={28} x2={4.4} y2={130} stroke={p.accent} strokeWidth="0.15" />
      <line x1={100.6} y1={28} x2={100.6} y2={130} stroke={p.accent} strokeWidth="0.15" />

      {/* Bottom flourish */}
      <line x1={14} y1={141} x2={91} y2={141} stroke={p.accent} strokeWidth="0.4" />
      <circle cx={52.5} cy={141} r={1.4} fill={p.accent} />
      <circle cx={52.5} cy={141} r={0.55} fill={p.paper} />

      <CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
    </CardSvg>
  );
}

// ===========================================================================
// 2. Locomotive Sunburst — streamlined loco + radiating rays.
// ===========================================================================

export function TemplateLocomotiveSunburst({
  booking,
  printMode,
  highlightDietary,
  size = "a6",
  style,
}: TemplateProps) {
  const p = paletteFor(printMode);

  // Generates an array of <line> elements forming a fan of rays.
  const rays = (cx: number, cy: number, dx: 1 | -1, dy: 1 | -1, count = 7) =>
    Array.from({ length: count }, (_, i) => {
      const angle = (i / (count - 1)) * 70 + 10; // 10..80 deg
      const rad = (angle * Math.PI) / 180;
      const x2 = cx + Math.cos(rad) * 24 * dx;
      const y2 = cy + Math.sin(rad) * 24 * dy;
      return (
        <line
          key={i}
          x1={cx}
          y1={cy}
          x2={x2}
          y2={y2}
          stroke={p.accent}
          strokeWidth={i % 2 === 0 ? 0.45 : 0.2}
          opacity={p.isBw ? 1 : 0.85}
        />
      );
    });

  if (size === "a5") {
    return (
      <CardSvg palette={p} size="a5" style={style}>
        {/* Sunburst rays in the four corners */}
        <g>{rays(0, 0, 1, 1)}</g>
        <g>{rays(210, 0, -1, 1)}</g>
        <g>{rays(0, 148, 1, -1)}</g>
        <g>{rays(210, 148, -1, -1)}</g>

        {/* Loco silhouette across the top centre — geometric, stylised */}
        <g transform="translate(87 4)" fill={p.accentDeep}>
          {/* Boiler + cab */}
          <rect x={0} y={3.2} width={36} height={4.6} rx={0.6} />
          <rect x={26} y={0.6} width={10} height={3.2} rx={0.3} />
          {/* Smokestack */}
          <rect x={6} y={0} width={2.4} height={3.6} />
          <rect x={5} y={0} width={4.4} height={0.6} />
          {/* Front lamp */}
          <rect x={-0.6} y={4.4} width={1.4} height={2.2} rx={0.25} />
          {/* Wheels */}
          <circle cx={4} cy={9.4} r={2.0} />
          <circle cx={4} cy={9.4} r={0.9} fill={p.paper} />
          <circle cx={12} cy={9.4} r={2.4} />
          <circle cx={12} cy={9.4} r={1.0} fill={p.paper} />
          <circle cx={20} cy={9.4} r={2.4} />
          <circle cx={20} cy={9.4} r={1.0} fill={p.paper} />
          <circle cx={32} cy={9.4} r={1.6} />
          {/* Coupling rod */}
          <line x1={4} y1={9.4} x2={32} y2={9.4} stroke={p.accent} strokeWidth="0.5" />
          {/* Smoke puffs */}
          <g fill={p.inkSoft} opacity={p.isBw ? 1 : 0.45}>
            <circle cx={7} cy={-1} r={1.2} />
            <circle cx={4} cy={-2.2} r={1.6} />
            <circle cx={1} cy={-1} r={1} />
          </g>
        </g>

        {/* Track baseline under the loco */}
        <line x1={8} y1={16.8} x2={202} y2={16.8} stroke={p.rule} strokeWidth="0.5" />
        <line x1={8} y1={17.6} x2={202} y2={17.6} stroke={p.rule} strokeWidth="0.15" />

        {/* Bottom rule above the footer text */}
        <line x1={8} y1={140} x2={202} y2={140} stroke={p.rule} strokeWidth="0.5" />

        <A5CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
      </CardSvg>
    );
  }

  return (
    <CardSvg palette={p} style={style}>
      {/* Sunburst rays in the four corners */}
      <g>{rays(0, 0, 1, 1)}</g>
      <g>{rays(105, 0, -1, 1)}</g>
      <g>{rays(0, 148, 1, -1)}</g>
      <g>{rays(105, 148, -1, -1)}</g>

      {/* Loco silhouette across the top — geometric, stylised */}
      <g
        transform="translate(28 4)"
        fill={p.accentDeep}
      >
        {/* Boiler + cab */}
        <rect x={0} y={3.2} width={36} height={4.6} rx={0.6} />
        <rect x={26} y={0.6} width={10} height={3.2} rx={0.3} />
        {/* Smokestack */}
        <rect x={6} y={0} width={2.4} height={3.6} />
        <rect x={5} y={0} width={4.4} height={0.6} />
        {/* Front lamp */}
        <rect x={-0.6} y={4.4} width={1.4} height={2.2} rx={0.25} />
        {/* Wheels */}
        <circle cx={4} cy={9.4} r={2.0} />
        <circle cx={4} cy={9.4} r={0.9} fill={p.paper} />
        <circle cx={12} cy={9.4} r={2.4} />
        <circle cx={12} cy={9.4} r={1.0} fill={p.paper} />
        <circle cx={20} cy={9.4} r={2.4} />
        <circle cx={20} cy={9.4} r={1.0} fill={p.paper} />
        <circle cx={32} cy={9.4} r={1.6} />
        {/* Coupling rod */}
        <line x1={4} y1={9.4} x2={32} y2={9.4} stroke={p.accent} strokeWidth="0.5" />
        {/* Smoke puffs */}
        <g fill={p.inkSoft} opacity={p.isBw ? 1 : 0.45}>
          <circle cx={7} cy={-1} r={1.2} />
          <circle cx={4} cy={-2.2} r={1.6} />
          <circle cx={1} cy={-1} r={1} />
        </g>
      </g>

      {/* Track baseline */}
      <line x1={6} y1={16.8} x2={99} y2={16.8} stroke={p.rule} strokeWidth="0.5" />
      <line x1={6} y1={17.6} x2={99} y2={17.6} stroke={p.rule} strokeWidth="0.15" />

      {/* Bottom rule above the footer text */}
      <line x1={6} y1={140} x2={99} y2={140} stroke={p.rule} strokeWidth="0.5" />

      <CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
    </CardSvg>
  );
}

// ===========================================================================
// 3. Carriage Panelling — wood-veneer panels + brass fittings.
// ===========================================================================

export function TemplateCarriagePanelling({
  booking,
  printMode,
  highlightDietary,
  size = "a6",
  style,
}: TemplateProps) {
  const p = paletteFor(printMode);
  const veneer = printMode === "bw" ? "#ffffff" : "#e9d3a3";
  const panelInk = printMode === "bw" ? "#000000" : "#7a5a2c";

  if (size === "a5") {
    return (
      <CardSvg palette={p} size="a5" style={style}>
        {/* Outer + inner double frame */}
        <rect x={5} y={5} width={200} height={138} fill="none" stroke={p.accent} strokeWidth="0.8" />
        <rect x={6.6} y={6.6} width={196.8} height={134.8} fill="none" stroke={panelInk} strokeWidth="0.3" />

        {/* Four veneer corner panels with grain lines + brass rivets */}
        {[
          { x: 7.5, y: 7.5 },
          { x: 192.5, y: 7.5 },
          { x: 7.5, y: 130.5 },
          { x: 192.5, y: 130.5 },
        ].map((c, i) => (
          <g key={i}>
            <rect x={c.x} y={c.y} width={10} height={10} fill={veneer} stroke={panelInk} strokeWidth="0.2" />
            <line x1={c.x + 1} y1={c.y + 2.5} x2={c.x + 9} y2={c.y + 2.5} stroke={panelInk} strokeWidth="0.1" />
            <line x1={c.x + 1} y1={c.y + 5}   x2={c.x + 9} y2={c.y + 5}   stroke={panelInk} strokeWidth="0.1" />
            <line x1={c.x + 1} y1={c.y + 7.5} x2={c.x + 9} y2={c.y + 7.5} stroke={panelInk} strokeWidth="0.1" />
            {[
              [c.x + 1.3, c.y + 1.3],
              [c.x + 8.7, c.y + 1.3],
              [c.x + 1.3, c.y + 8.7],
              [c.x + 8.7, c.y + 8.7],
            ].map(([rx, ry], j) => (
              <circle key={j} cx={rx} cy={ry} r={0.45} fill={p.accent} stroke={panelInk} strokeWidth="0.1" />
            ))}
          </g>
        ))}

        {/* Mid-edge flutings along the top + bottom long edges */}
        {[30, 50, 70, 90, 120, 140, 160, 180].map((x) => (
          <g key={x}>
            <line x1={x} y1={5} x2={x} y2={6.5} stroke={p.accent} strokeWidth="0.25" />
            <line x1={x} y1={141.5} x2={x} y2={143} stroke={p.accent} strokeWidth="0.25" />
          </g>
        ))}

        {/* Top centre medallion — initial P */}
        <g transform="translate(105 11)">
          <circle r={3.2} fill={veneer} stroke={panelInk} strokeWidth="0.3" />
          <text fontFamily="Times" fontSize="3.2" fontWeight="bold" textAnchor="middle" dy={1.1} fill={p.accentDeep}>
            P
          </text>
        </g>

        {/* Bottom centre rosette */}
        <g transform="translate(105 137)">
          <circle r={1.6} fill={p.accent} />
          <circle r={0.7} fill={p.paper} />
        </g>

        <A5CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
      </CardSvg>
    );
  }

  return (
    <CardSvg palette={p} style={style}>
      {/* Outer + inner double frame */}
      <rect x={5} y={5} width={95} height={138} fill="none" stroke={p.accent} strokeWidth="0.8" />
      <rect x={6.6} y={6.6} width={91.8} height={134.8} fill="none" stroke={panelInk} strokeWidth="0.3" />

      {/* Four veneer corner panels with grain lines + brass rivets */}
      {[
        { x: 7.5, y: 7.5 },
        { x: 87.5, y: 7.5 },
        { x: 7.5, y: 130.5 },
        { x: 87.5, y: 130.5 },
      ].map((c, i) => (
        <g key={i}>
          <rect
            x={c.x}
            y={c.y}
            width={10}
            height={10}
            fill={veneer}
            stroke={panelInk}
            strokeWidth="0.2"
          />
          {/* Wood grain — three horizontal stripes */}
          <line x1={c.x + 1} y1={c.y + 2.5} x2={c.x + 9} y2={c.y + 2.5} stroke={panelInk} strokeWidth="0.1" />
          <line x1={c.x + 1} y1={c.y + 5}   x2={c.x + 9} y2={c.y + 5}   stroke={panelInk} strokeWidth="0.1" />
          <line x1={c.x + 1} y1={c.y + 7.5} x2={c.x + 9} y2={c.y + 7.5} stroke={panelInk} strokeWidth="0.1" />
          {/* Brass rivets at each corner of the panel */}
          {[
            [c.x + 1.3, c.y + 1.3],
            [c.x + 8.7, c.y + 1.3],
            [c.x + 1.3, c.y + 8.7],
            [c.x + 8.7, c.y + 8.7],
          ].map(([rx, ry], j) => (
            <circle
              key={j}
              cx={rx}
              cy={ry}
              r={0.45}
              fill={p.accent}
              stroke={panelInk}
              strokeWidth="0.1"
            />
          ))}
        </g>
      ))}

      {/* Mid-edge flutings — small parallel lines along each long edge */}
      {[20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120].map((y) => (
        <g key={y}>
          <line x1={5} y1={y} x2={6.5} y2={y} stroke={p.accent} strokeWidth="0.25" />
          <line x1={98.5} y1={y} x2={100} y2={y} stroke={p.accent} strokeWidth="0.25" />
        </g>
      ))}

      {/* Top centre medallion — initial P */}
      <g transform="translate(52.5 11)">
        <circle r={3.2} fill={veneer} stroke={panelInk} strokeWidth="0.3" />
        <text
          fontFamily="Times"
          fontSize="3.2"
          fontWeight="bold"
          textAnchor="middle"
          dy={1.1}
          fill={p.accentDeep}
        >
          P
        </text>
      </g>

      {/* Bottom centre rosette */}
      <g transform="translate(52.5 137)">
        <circle r={1.6} fill={p.accent} />
        <circle r={0.7} fill={p.paper} />
      </g>

      <CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
    </CardSvg>
  );
}

// ===========================================================================
// 4. Streamline Moderne — flowing speed-lines + smoke trails.
// ===========================================================================

export function TemplateStreamlineModerne({
  booking,
  printMode,
  highlightDietary,
  size = "a6",
  style,
}: TemplateProps) {
  const p = paletteFor(printMode);

  if (size === "a5") {
    return (
      <CardSvg palette={p} size="a5" style={style}>
        {/* Three parallel speed-lines top */}
        <path d="M 6 7 L 204 7" stroke={p.accent} strokeWidth="0.9" fill="none" />
        <path d="M 8 9.5 L 202 9.5" stroke={p.accent} strokeWidth="0.5" fill="none" />
        <path d="M 10 11.5 L 200 11.5" stroke={p.accent} strokeWidth="0.25" fill="none" />

        {/* Mirror at the bottom */}
        <path d="M 6 141 L 204 141" stroke={p.accent} strokeWidth="0.9" fill="none" />
        <path d="M 8 138.5 L 202 138.5" stroke={p.accent} strokeWidth="0.5" fill="none" />
        <path d="M 10 136.5 L 200 136.5" stroke={p.accent} strokeWidth="0.25" fill="none" />

        {/* Curling smoke trails down each long edge — Bezier loops */}
        <g stroke={p.accentDeep} strokeWidth="0.4" fill="none" opacity={p.isBw ? 1 : 0.85}>
          <path d="M 3 22 Q 8 30 3 38 Q -2 46 3 54 Q 8 62 3 70 Q -2 78 3 86 Q 8 94 3 102 Q -2 110 3 118 Q 8 126 3 132" />
          <path d="M 207 22 Q 202 30 207 38 Q 212 46 207 54 Q 202 62 207 70 Q 212 78 207 86 Q 202 94 207 102 Q 212 110 207 118 Q 202 126 207 132" />
        </g>

        {/* Speed-arrow chevrons centre top + bottom for extra motion */}
        {[
          { x: 105, y: 4.5, rot: 0 },
          { x: 105, y: 143.5, rot: 180 },
        ].map((c, i) => (
          <g key={i} transform={`translate(${c.x} ${c.y}) rotate(${c.rot})`}>
            <path d="M -3.2 0 L 0 -2 L 3.2 0" stroke={p.accentDeep} strokeWidth="0.6" fill="none" />
            <path d="M -2 1.5 L 0 0 L 2 1.5" stroke={p.accentDeep} strokeWidth="0.4" fill="none" />
          </g>
        ))}

        <A5CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
      </CardSvg>
    );
  }

  return (
    <CardSvg palette={p} style={style}>
      {/* Three parallel speed-lines top */}
      <path d="M 4 7 L 101 7"     stroke={p.accent} strokeWidth="0.9"  fill="none" />
      <path d="M 6 9.5 L 99 9.5"  stroke={p.accent} strokeWidth="0.5"  fill="none" />
      <path d="M 8 11.5 L 97 11.5" stroke={p.accent} strokeWidth="0.25" fill="none" />

      {/* Mirror at the bottom */}
      <path d="M 4 141 L 101 141"  stroke={p.accent} strokeWidth="0.9"  fill="none" />
      <path d="M 6 138.5 L 99 138.5" stroke={p.accent} strokeWidth="0.5"  fill="none" />
      <path d="M 8 136.5 L 97 136.5" stroke={p.accent} strokeWidth="0.25" fill="none" />

      {/* Curling smoke trails down each long edge — Bezier loops */}
      <g stroke={p.accentDeep} strokeWidth="0.4" fill="none" opacity={p.isBw ? 1 : 0.85}>
        <path d="M 3 22 Q 8 30 3 38 Q -2 46 3 54 Q 8 62 3 70 Q -2 78 3 86 Q 8 94 3 102 Q -2 110 3 118 Q 8 126 3 132" />
        <path d="M 102 22 Q 97 30 102 38 Q 107 46 102 54 Q 97 62 102 70 Q 107 78 102 86 Q 97 94 102 102 Q 107 110 102 118 Q 97 126 102 132" />
      </g>

      {/* Speed-arrow chevrons in centre top + bottom for extra motion */}
      {[
        { x: 52.5, y: 4.5, rot: 0 },
        { x: 52.5, y: 143.5, rot: 180 },
      ].map((c, i) => (
        <g key={i} transform={`translate(${c.x} ${c.y}) rotate(${c.rot})`}>
          <path d="M -3.2 0 L 0 -2 L 3.2 0" stroke={p.accentDeep} strokeWidth="0.6" fill="none" />
          <path d="M -2 1.5 L 0 0 L 2 1.5"  stroke={p.accentDeep} strokeWidth="0.4" fill="none" />
        </g>
      ))}

      <CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
    </CardSvg>
  );
}

// ===========================================================================
// 5. Art Deco Bouquet — lotus fans + engine-wheel rosettes.
// ===========================================================================

export function TemplateArtDecoBouquet({
  booking,
  printMode,
  highlightDietary,
  size = "a6",
  style,
}: TemplateProps) {
  const p = paletteFor(printMode);

  // A "lotus fan" cluster of stacked stylised petals.
  const lotus = (cx: number, cy: number, sx: 1 | -1, sy: 1 | -1) => (
    <g transform={`translate(${cx} ${cy}) scale(${sx} ${sy})`}>
      <path d="M 0 0 Q 4 -2 8 0 L 4 0 Z" fill={p.accent} />
      <path d="M 0 0 Q 5 -5 10 -2 L 6 -1 Z" fill={p.accent} opacity={p.isBw ? 1 : 0.85} />
      <path d="M 0 0 Q 4 -8 9 -7 L 5 -3 Z" fill={p.accent} opacity={p.isBw ? 1 : 0.7} />
      <path d="M 0 0 Q 1 -8 6 -10 L 3 -5 Z" fill={p.accent} opacity={p.isBw ? 1 : 0.55} />
      <path d="M 0 0 Q -2 -7 1 -11 L 1 -6 Z" fill={p.accent} opacity={p.isBw ? 1 : 0.4} />
      <circle cx={0} cy={0} r={1.4} fill={p.accentDeep} />
      <circle cx={0} cy={0} r={0.6} fill={p.accent} />
    </g>
  );

  // Engine-wheel rosette — a ring with eight spokes.
  const wheel = (cx: number, cy: number) => (
    <g transform={`translate(${cx} ${cy})`}>
      <circle r={2.4} fill="none" stroke={p.accent} strokeWidth="0.3" />
      <circle r={1.5} fill="none" stroke={p.accent} strokeWidth="0.2" />
      <circle r={0.55} fill={p.accent} />
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return (
          <line
            key={i}
            x1={Math.cos(a) * 0.7}
            y1={Math.sin(a) * 0.7}
            x2={Math.cos(a) * 2.2}
            y2={Math.sin(a) * 2.2}
            stroke={p.accent}
            strokeWidth="0.25"
          />
        );
      })}
    </g>
  );

  if (size === "a5") {
    return (
      <CardSvg palette={p} size="a5" style={style}>
        {/* Outer rectangle frame */}
        <rect x={6} y={6} width={198} height={136} fill="none" stroke={p.rule} strokeWidth="0.3" />

        {/* Four corner lotuses */}
        {lotus(7, 14, 1, 1)}
        {lotus(203, 14, -1, 1)}
        {lotus(7, 134, 1, -1)}
        {lotus(203, 134, -1, -1)}

        {/* Mid-edge wheels — one on each short-edge centre */}
        {wheel(7, 74)}
        {wheel(203, 74)}

        {/* Top + bottom curved swags connecting the lotuses */}
        <path d="M 16 8 Q 105 14 194 8" stroke={p.accent} strokeWidth="0.4" fill="none" />
        <path d="M 16 140 Q 105 134 194 140" stroke={p.accent} strokeWidth="0.4" fill="none" />

        {/* Three small dots above the title bar */}
        <g fill={p.accent}>
          <circle cx={101} cy={5} r={0.5} />
          <circle cx={105} cy={5} r={0.7} />
          <circle cx={109} cy={5} r={0.5} />
        </g>

        <A5CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
      </CardSvg>
    );
  }

  return (
    <CardSvg palette={p} style={style}>
      {/* Outer rectangle frame */}
      <rect x={6} y={6} width={93} height={136} fill="none" stroke={p.rule} strokeWidth="0.3" />

      {/* Four corner lotuses */}
      {lotus(7, 14, 1, 1)}
      {lotus(98, 14, -1, 1)}
      {lotus(7, 134, 1, -1)}
      {lotus(98, 134, -1, -1)}

      {/* Mid-edge wheels — one in each long-edge centre */}
      {wheel(7, 74)}
      {wheel(98, 74)}

      {/* Top + bottom curved swag connecting the lotuses */}
      <path d="M 14 8 Q 52.5 14 91 8" stroke={p.accent} strokeWidth="0.4" fill="none" />
      <path d="M 14 140 Q 52.5 134 91 140" stroke={p.accent} strokeWidth="0.4" fill="none" />

      {/* Three small dots above the title bar */}
      <g fill={p.accent}>
        <circle cx={48.5} cy={5} r={0.5} />
        <circle cx={52.5} cy={5} r={0.7} />
        <circle cx={56.5} cy={5} r={0.5} />
      </g>

      <CardContent booking={booking} palette={p} highlightDietary={highlightDietary} />
    </CardSvg>
  );
}

// ===========================================================================
// 6. Plain (legacy) — a faithful repro of the old SSRS menu card. No borders,
//    no branding, plain Helvetica type. Two-panel on A5 (info left, menu
//    right, à la the "Menu Cards - A5" RDL); a single stacked column on A6.
//    Always plain black on white — ignores printMode and the dietary banner.
// ===========================================================================

const PLAIN_PALETTE: Palette = {
  paper: "#ffffff",
  ink: "#000000",
  inkSoft: "#000000",
  rule: "#000000",
  accent: "#000000",
  accentDeep: "#000000",
  diet: "#000000",
  dietInk: "#ffffff",
  isBw: true,
};

export function TemplatePlainLegacy({
  booking,
  size = "a6",
  style,
}: TemplateProps) {
  const p = PLAIN_PALETTE;
  const menuLines = booking.menus
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const seating = [
    shortCarriage(booking.carriage),
    booking.table_name ? `Table ${booking.table_name}` : "",
    booking.seats,
  ]
    .filter(Boolean)
    .join("   ");
  const name =
    booking.account_name.length > 40
      ? booking.account_name.slice(0, 38) + "…"
      : booking.account_name;

  if (size === "a5") {
    return (
      <CardSvg palette={p} size="a5" style={style}>
        {/* Left panel: journey type, customer, seating (no labels — as SSRS) */}
        <text x={12} y={16} fontFamily="Helvetica" fontSize="4.0" fill={p.ink}>
          {booking.journey_type}
        </text>
        <text x={12} y={30} fontFamily="Helvetica" fontSize="4.0" fill={p.ink}>
          {name}
        </text>
        <text x={12} y={44} fontFamily="Helvetica" fontSize="4.0" fill={p.ink}>
          {seating}
        </text>

        {/* Right panel: the menu block, verbatim lines */}
        <g fontFamily="Helvetica" fontSize="3.6" fill={p.ink}>
          {menuLines.slice(0, 28).map((ln, i) => (
            <text key={i} x={110} y={14 + i * 4.6}>
              {ln.length > 52 ? ln.slice(0, 50) + "…" : ln}
            </text>
          ))}
        </g>
      </CardSvg>
    );
  }

  // A6 portrait — single stacked column.
  return (
    <CardSvg palette={p} size="a6" style={style}>
      <text x={10} y={16} fontFamily="Helvetica" fontSize="3.4" fill={p.ink}>
        {booking.journey_type}
      </text>
      <text x={10} y={24} fontFamily="Helvetica" fontSize="3.4" fill={p.ink}>
        {name}
      </text>
      <text x={10} y={32} fontFamily="Helvetica" fontSize="3.0" fill={p.ink}>
        {seating}
      </text>
      <g fontFamily="Helvetica" fontSize="2.8" fill={p.ink}>
        {menuLines.slice(0, 28).map((ln, i) => (
          <text key={i} x={10} y={42 + i * 3.4}>
            {ln.length > 52 ? ln.slice(0, 50) + "…" : ln}
          </text>
        ))}
      </g>
    </CardSvg>
  );
}

// ===========================================================================
// 7. Travel & Treat — a clean, simple card for Fish & Chips / Travel & Treats
//    services. Those have no coach/table plan and only light pre-orders, so
//    this drops the coach/table/seats + course structure and just shows the
//    guest, party size, the service, and any pre-ordered drinks/extras.
//    Centred layout; works at both A6 portrait and A5 landscape.
// ===========================================================================

export function TemplateTravelTreat({
  booking,
  printMode,
  size = "a6",
  style,
}: TemplateProps) {
  const p = paletteFor(printMode);
  const w = size === "a5" ? 210 : 105;
  const cx = w / 2;
  const half = w * 0.42;
  const pre = parseMenuLines(booking.menus);
  const descLines = wrapText(booking.journey_description, size === "a5" ? 60 : 40);
  const preMax = size === "a5" ? 16 : 12;
  const visiblePre = pre.slice(0, preMax);
  const preTruncated = pre.length > preMax;
  const itemMax = size === "a5" ? 70 : 40;

  return (
    <CardSvg palette={p} size={size} style={style}>
      {/* Header */}
      <text x={cx} y={13} textAnchor="middle" fontFamily="Times" fontSize="3.0" fontWeight="bold" letterSpacing="1" fill={p.accentDeep}>
        NORTH YORKSHIRE MOORS RAILWAY
      </text>
      <line x1={cx - half} y1={16} x2={cx + half} y2={16} stroke={p.accent} strokeWidth="0.5" />
      <line x1={cx - half} y1={16.8} x2={cx + half} y2={16.8} stroke={p.accent} strokeWidth="0.15" />

      {/* Service */}
      <text x={cx} y={26} textAnchor="middle" fontFamily="Times" fontSize="4.4" fontWeight="bold" fill={p.ink}>
        {booking.service_day_label}
      </text>
      {descLines.slice(0, 2).map((ln, i) => (
        <text key={i} x={cx} y={32 + i * 4.6} textAnchor="middle" fontFamily="Times" fontSize="3.0" fontStyle="italic" fill={p.inkSoft}>
          {ln}
        </text>
      ))}

      {/* Guest + party */}
      <line x1={cx - half} y1={46} x2={cx + half} y2={46} stroke={p.rule} strokeWidth="0.25" />
      <text x={cx} y={56} textAnchor="middle" fontFamily="Times" fontSize="5.0" fontWeight="bold" fill={p.ink}>
        {booking.account_name.length > 40 ? booking.account_name.slice(0, 38) + "…" : booking.account_name}
      </text>
      {booking.footfall != null && booking.footfall > 0 && (
        <text x={cx} y={63} textAnchor="middle" fontFamily="Times" fontSize="3.2" fontStyle="italic" fill={p.inkSoft}>
          {booking.footfall} {booking.footfall === 1 ? "guest" : "guests"}
        </text>
      )}

      {/* Pre-orders */}
      <rect x={cx - 10} y={70} width={20} height={5.4} rx={0.6} fill={p.accent} />
      <text x={cx} y={73.8} textAnchor="middle" fontFamily="Times" fontSize="2.8" fontWeight="bold" letterSpacing="0.6" fill={p.paper}>
        PRE-ORDER
      </text>
      <g fontFamily="Times">
        {visiblePre.length === 0 ? (
          <text x={cx} y={84} textAnchor="middle" fontSize="3.0" fontStyle="italic" fill={p.inkSoft}>
            No pre-orders recorded
          </text>
        ) : (
          visiblePre.map((ln, i) => {
            const label = (ln.qty ? ln.qty + "  " : "") + (ln.course ? ln.course + " — " : "") + ln.item;
            return (
              <text key={i} x={cx} y={82 + i * 4.2} textAnchor="middle" fontSize="3.0" fill={p.ink}>
                {label.length > itemMax ? label.slice(0, itemMax - 1) + "…" : label}
              </text>
            );
          })
        )}
        {preTruncated && (
          <text x={cx} y={82 + visiblePre.length * 4.2} textAnchor="middle" fontSize="2.4" fontStyle="italic" fill={p.inkSoft}>
            (… {pre.length - preMax} more)
          </text>
        )}
      </g>

      {/* Footer */}
      <text x={cx - half} y={144} fontFamily="Times" fontSize="2.2" fill={p.inkSoft}>
        Booking {booking.booking_reference}
      </text>
      <text x={cx + half} y={144} textAnchor="end" fontFamily="Times" fontSize="2.2" fill={p.inkSoft}>
        Departs {booking.service_time_label.slice(-5)}
      </text>
    </CardSvg>
  );
}

// ===========================================================================
// Registry + deterministic random pick
// ===========================================================================

export type TemplateRenderer = (props: TemplateProps) => ReactElement;

/**
 * Maps each TemplateStyle key (other than `random-mix`) to its renderer.
 * `random-mix` is resolved before lookup by `pickTemplate()`.
 */
export const TEMPLATE_RENDERERS: Record<
  Exclude<TemplateStyle, "random-mix">,
  TemplateRenderer
> = {
  "pullman-classic": TemplatePullmanClassic,
  "locomotive-sunburst": TemplateLocomotiveSunburst,
  "carriage-panelling": TemplateCarriagePanelling,
  "streamline-moderne": TemplateStreamlineModerne,
  "art-deco-bouquet": TemplateArtDecoBouquet,
  "plain-legacy": TemplatePlainLegacy,
  "travel-treat": TemplateTravelTreat,
};

const TEMPLATE_KEYS: Exclude<TemplateStyle, "random-mix">[] = [
  "pullman-classic",
  "locomotive-sunburst",
  "carriage-panelling",
  "streamline-moderne",
  "art-deco-bouquet",
];

/**
 * Resolve `random-mix` to a stable choice per booking. Reprinting the same
 * booking yields the same template — no surprise remixes between runs.
 *
 * Hash uses the booking_reference × Knuth's multiplicative constant
 * (2654435761) to distribute consecutive references across the five buckets.
 */
export function pickTemplate(
  selection: TemplateStyle,
  booking: MenuCardBooking
): Exclude<TemplateStyle, "random-mix"> {
  if (selection !== "random-mix") return selection;
  const ref = booking.booking_reference || 1;
  const idx = Math.abs(ref * 2654435761) % TEMPLATE_KEYS.length;
  return TEMPLATE_KEYS[idx];
}

/**
 * Convenience: render the right template for a booking.
 * Used by the modal preview AND by the PDF builder.
 */
export function renderTemplate(args: {
  booking: MenuCardBooking;
  selection: TemplateStyle;
  printMode: PrintMode;
  highlightDietary: boolean;
  scaleFoodToFit?: boolean;
  size?: CardSize;
  style?: CSSProperties;
}): ReactElement {
  // Batch-level flag read by the shared CardContent / A5CardContent. Set here,
  // synchronously, immediately before the returned element is rendered.
  scaleFoodToFitActive = args.scaleFoodToFit ?? false;
  const concrete = pickTemplate(args.selection, args.booking);
  const Renderer = TEMPLATE_RENDERERS[concrete];
  return (
    <Renderer
      booking={args.booking}
      printMode={args.printMode}
      highlightDietary={args.highlightDietary}
      size={args.size}
      style={args.style}
    />
  );
}
